import { StudentProfileResponse } from '../../../services/studentProfileService';
import { FEE_INVOICE_KIND_LABEL } from '../../../types/fees';
import { statusLabel } from '../../common/StatusChip';
import { placementName } from './enrollmentPlacement';
import { PAYMENT_METHOD_LABEL } from '../../../utils/paymentMethods';

/**
 * One chronology for a person — admissions, enrollments, fees and letters —
 * newest first. Read by the Timeline tab and Overview's recent activity.
 */

export type ProfileEventKind = 'admission' | 'enrollment' | 'fees' | 'letter';

export interface ProfileEvent {
  at: string;
  kind: ProfileEventKind;
  title: string;
  detail?: string;
}

/** snake_case → "Snake case", for operational events without a label. */
const humanize = (k: string) => k.replace(/[_-]+/g, ' ').trim().replace(/^./, c => c.toUpperCase());

/** `money` formats amounts in the school's currency (useCurrencyFormat). */
export function buildProfileEvents(data: StudentProfileResponse, money: (n: number) => string): ProfileEvent[] {
  const out: ProfileEvent[] = [];

  // Admissions: each application's own timeline — status moves and
  // operational events (admit card issued, details corrected, …).
  data.admissions.forEach((app: any) => {
    const cycle = typeof app.cycleId === 'object' ? app.cycleId?.name : undefined;
    (app.timeline || []).forEach((t: any) => {
      if (!t?.at) return;
      const what = t.event
        ? t.label || humanize(t.event)
        : t.to ? statusLabel({ kind: 'application', value: t.to }) : null;
      if (!what) return;
      out.push({ at: t.at, kind: 'admission', title: what, detail: [cycle, t.reason].filter(Boolean).join(' · ') || undefined });
    });
  });

  // Enrollments: joining a class, roll-number changes, and leaving it.
  data.enrollments.forEach((e: any) => {
    const placement = placementName(e);
    const where = [placement, e.sessionId?.name].filter(Boolean).join(' · ');
    const joined = e.enrolledOn || e.createdAt;
    if (joined) {
      const how = e.transferredFromEnrollmentId ? 'Moved to' : e.promotedFromEnrollmentId ? 'Promoted to' : 'Enrolled in';
      out.push({
        at: joined,
        kind: 'enrollment',
        title: `${how} ${placement || 'class'}`,
        detail: [e.sessionId?.name, e.rollNumber != null ? `Roll ${e.rollNumber}` : null].filter(Boolean).join(' · ') || undefined,
      });
    }
    (e.rollHistory || []).forEach((r: any) => {
      if (r?.at) out.push({ at: r.at, kind: 'enrollment', title: `Roll number changed, ${r.from} → ${r.to}`, detail: where || undefined });
    });
    if (e.exitedOn && e.enrollmentStatus !== 'enrolled') {
      out.push({
        at: e.exitedOn,
        kind: 'enrollment',
        title: statusLabel({ kind: 'enrollment', value: e.enrollmentStatus }),
        detail: [where, e.exitReason].filter(Boolean).join(' · ') || undefined,
      });
    }
  });

  // Fees: challans issued and payments received.
  data.fees.invoices.forEach((inv: any) => {
    const when = inv.issuedAt || inv.createdAt;
    if (!when) return;
    out.push({
      at: when,
      kind: 'fees',
      title: `${FEE_INVOICE_KIND_LABEL[inv.kind as keyof typeof FEE_INVOICE_KIND_LABEL] || 'Challan'} issued, ${money(inv.totals?.net ?? 0)}`,
      detail: [inv.invoiceNumber && `Challan ${inv.invoiceNumber}`, inv.period?.label].filter(Boolean).join(' · ') || undefined,
    });
  });
  data.fees.payments.forEach((p: any) => {
    if (!p.receivedAt) return;
    out.push({
      at: p.receivedAt,
      kind: 'fees',
      title: `Payment received, ${money(p.amount ?? 0)}`,
      detail: [p.receiptNumber[p.method as keyof typeof PAYMENT_METHOD_LABEL]].filter(Boolean).join(' · ') || undefined,
    });
  });

  // Letters.
  data.letters.forEach((l: any) => {
    if (!l.issuedAt) return;
    out.push({
      at: l.issuedAt,
      kind: 'letter',
      title: [l.categoryId?.label, l.title].filter(Boolean).join(': ') || 'Letter issued',
      detail: l.reason || undefined,
    });
  });

  return out.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}
